import React, {useCallback} from 'react';
import {
  TBoolean,
  TEnum,
  TNumber,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  withSystem,
  Plugins,
  withMeta,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ATTR_AUTOFOCUS_PATH,
  ATTR_ID_PATH,
  ATTR_PLACEHOLDER_PATH,
  FORM_DEFAULT_LABEL,
  TEXT_TYPE_LABELS,
} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {useUpdateSiblingLabel} from './formHooks';
import {usysFieldType, USYS_FIELD_PATH} from '@packages/systems/users/usysWfdl';
import {UserFormRequired} from '../UserFormRequired';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
  paddingBottom: tokens.space[2],
});

const formFields = withSystem(
  {labelVariant: 'hidden'},
  TRecord({
    name: withSystem(
      {label: 'Name', defaultValue: FORM_DEFAULT_LABEL, isLocalizable: true},
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
    ),
    type: withSystem({defaultValue: 'input', private: true}, TText),
  })
);

const AttrFields = TRecord({
  id: withSystem(
    {
      defaultValue: FORM_DEFAULT_LABEL,
      private: true,
      label: 'ID',
      isBindable: false,
      isLocalizable: true,
    },
    TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput')))
  ),
  maxlength: withSystem({defaultValue: 256, private: true}, TNumber),
  'data-name': withSystem(
    {defaultValue: FORM_DEFAULT_LABEL, private: true, isLocalizable: true},
    TText
  ),
  placeholder: withMeta(
    {placeholder: 'e.g. Enter your first name'},
    withSystem(
      {label: 'Placeholder', isFullWidth: true, isLocalizable: true},
      TText
    )
  ),
  type: withMeta(
    {
      variant: 'menu',
    },
    withMeta(
      {withoutLivePreview: true},
      withSystem(
        {
          private: true,
          label: 'Type',
          defaultValue: 'text',
          isLocalizable: true,
        },
        TEnum(TEXT_TYPE_LABELS)
      )
    )
  ),
  autofocus: withSystem(
    {
      defaultValue: false,
      label: 'Autofocus',
      labelVariant: 'hidden',
      isLocalizable: true,
    },
    TTypeConstructor(
      qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
    )
  ),
  name: TText,
  disabled: TBoolean,
  required: TBoolean,
  fieldType: TText,
});

const usysFields = {
  users: usysFieldType,
  form: formFields,
  attr: AttrFields,
} as const;

const getExpressionFromPath = (
  expr: ContextualExpression,
  path: ExpressionPath
) => {
  const expression = getIn(expr, path);
  if (!expression)
    throw Error(`failed to get expression at path: ${JSON.stringify(path)}`);
  return expression;
};

const getExpressionText = (
  expr: ContextualExpression,
  path: ExpressionPath
) => {
  return (
    ContextualExpressions.getText(
      stripAnn(getExpressionFromPath(expr, path))
    ) ?? ''
  );
};

const UserFormTextInputType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields: usysFields,
  render: function RenderUserFormTextInputType({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: any;
  }) {
    const id = getExpressionText(expr, ATTR_ID_PATH);
    useUpdateSiblingLabel(id);

    const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

    const placeholderExpr = getExpressionFromPath(expr, ATTR_PLACEHOLDER_PATH);
    const placeholderDispatch = useCallback(dispatchAt(ATTR_PLACEHOLDER_PATH), [
      dispatchAt,
    ]);

    const autofocusExpr = getExpressionFromPath(expr, ATTR_AUTOFOCUS_PATH);
    const autofocusDispatch = useCallback(dispatchAt(ATTR_AUTOFOCUS_PATH), [
      dispatchAt,
    ]);

    const userFieldId = getExpressionText(expr, USYS_FIELD_PATH);

    return (
      <Grid>
        {/* Placeholder */}
        <ExpressionEditor
          expression={placeholderExpr}
          dispatch={placeholderDispatch}
        />
        {/* Autofocus */}
        <ExpressionEditor
          expression={autofocusExpr}
          dispatch={autofocusDispatch}
        />
        {/* Required (read only) */}
        <UserFormRequired fieldId={userFieldId} />
      </Grid>
    );
  },
});

export default UserFormTextInputType;
